import { lazy, Suspense, useState, useRef } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster as ShadcnToaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import PageLoader from "@/components/PageLoader";
import SplashScreen from "@/components/SplashScreen";
import { TransitionVariant } from "@/lib/pageTransitionVariants";
import { useDisableSwipeBack } from "@/hooks/useDisableSwipeBack";
import { DesktopGate } from "@/components/DesktopGate";

// Lazy load all pages
const Home = lazy(() => import("./pages/Home"));
const Ricompense = lazy(() => import("./pages/Ricompense"));
const RewardDetail = lazy(() => import("./pages/RewardDetail"));
const MieiRiscatti = lazy(() => import("./pages/MieiRiscatti"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const OffertaDetail = lazy(() => import("./pages/OffertaDetail"));
const Notifiche = lazy(() => import("./pages/Notifiche"));
const NotFound = lazy(() => import("./pages/NotFound"));
const DatiPersonali = lazy(() => import("./pages/DatiPersonali"));
const LoginSicurezza = lazy(() => import("./pages/LoginSicurezza"));
const ModificaEmail = lazy(() => import("./pages/ModificaEmail"));
const ModificaTelefono = lazy(() => import("./pages/ModificaTelefono"));
const ModificaPassword = lazy(() => import("./pages/ModificaPassword"));
const TerminiCondizioni = lazy(() => import("./pages/TerminiCondizioni"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Profilo = lazy(() => import("./pages/Profilo"));
const Volantino = lazy(() => import("./pages/Volantino"));
const Punti = lazy(() => import("./pages/Punti"));
const PuntiExtra = lazy(() => import("./pages/PuntiExtra"));
const PuntiExtraDettaglio = lazy(() => import("./pages/PuntiExtraDettaglio"));
const Concierge = lazy(() => import("./pages/Concierge"));
const Appuntamento = lazy(() => import("./pages/Appuntamento"));
const NativeCallback = lazy(() => import("./pages/NativeCallback"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 2,
    },
  },
});

// ─── Depth map ────────────────────────────────────────────────────────────────
const DEPTH_MAP: Array<{ pattern: RegExp; depth: number }> = [
  { pattern: /^\/$/, depth: 0 },
  { pattern: /^\/login/, depth: 0 },
  { pattern: /^\/register/, depth: 0 },
  { pattern: /^\/forgot-password/, depth: 0 },
  { pattern: /^\/reset-password/, depth: 0 },
  { pattern: /^\/profilo/, depth: 1 },
  { pattern: /^\/concierge/, depth: 1 },
  { pattern: /^\/appuntamento/, depth: 2 },
  { pattern: /^\/ricompense$/, depth: 1 },
  { pattern: /^\/ricompense\//, depth: 2 },
  { pattern: /^\/miei-riscatti/, depth: 2 },
  { pattern: /^\/notifiche/, depth: 2 },
  { pattern: /^\/dati-personali/, depth: 2 },
  { pattern: /^\/login-sicurezza/, depth: 2 },
  { pattern: /^\/modifica-email/, depth: 3 },
  { pattern: /^\/modifica-telefono/, depth: 3 },
  { pattern: /^\/modifica-password/, depth: 3 },
  { pattern: /^\/termini-condizioni/, depth: 2 },
  { pattern: /^\/privacy-policy/, depth: 2 },
  { pattern: /^\/cookie-policy/, depth: 2 },
  { pattern: /^\/admin$/, depth: 3 },
  { pattern: /^\/admin\//, depth: 4 },
];

function getDepth(pathname: string): number {
  for (const { pattern, depth } of DEPTH_MAP) {
    if (pattern.test(pathname)) return depth;
  }
  return 1;
}

function getTransitionVariant(current: string, previous: string | null): TransitionVariant {
  if (!previous) return 'fade';
  const curr = getDepth(current);
  const prev = getDepth(previous);
  if (curr > prev) return 'forward';
  if (curr < prev) return 'back';
  return 'fade';
}


// ─── Animated Routes ──────────────────────────────────────────────────────────
function AnimatedRoutes() {
  const location = useLocation();
  const prevPath = useRef<string | null>(null);
  useDisableSwipeBack();

  const variant = getTransitionVariant(location.pathname, prevPath.current);
  prevPath.current = location.pathname;

  return (
    <Suspense fallback={<PageLoader />}>
      <AnimatePresence mode="wait" initial={false} custom={variant}>
        <Routes location={location} key={location.pathname}>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth" element={<Navigate to="/login" replace />} />
          <Route path="/native-callback" element={<NativeCallback />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />


          {/* Public routes with layout */}
          <Route path="/" element={<AppLayout><Home /></AppLayout>} />
          <Route path="/volantino" element={<AppLayout><Volantino /></AppLayout>} />
          <Route path="/offerta/:slug" element={<OffertaDetail />} />
          <Route path="/concierge" element={<AppLayout><Concierge /></AppLayout>} />
          <Route path="/appuntamento" element={<Appuntamento />} />
          <Route path="/dati-personali" element={<DatiPersonali />} />
          <Route path="/login-sicurezza" element={<LoginSicurezza />} />
          <Route path="/modifica-email" element={<ModificaEmail />} />
          <Route path="/modifica-telefono" element={<ModificaTelefono />} />
          <Route path="/modifica-password" element={<ModificaPassword />} />
          <Route path="/termini-condizioni" element={<TerminiCondizioni />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/notifiche" element={
            <ProtectedRoute><Notifiche /></ProtectedRoute>
          } />

          {/* Protected routes */}
          <Route path="/ricompense" element={
            <ProtectedRoute><AppLayout><Ricompense /></AppLayout></ProtectedRoute>
          } />
          <Route path="/punti" element={
            <ProtectedRoute><AppLayout><Punti /></AppLayout></ProtectedRoute>
          } />
          <Route path="/punti-extra" element={
            <ProtectedRoute><AppLayout><PuntiExtra /></AppLayout></ProtectedRoute>
          } />
          <Route path="/punti-extra/invita" element={
            <ProtectedRoute><AppLayout><PuntiExtraDettaglio /></AppLayout></ProtectedRoute>
          } />
          <Route path="/ricompense/:id" element={
            <ProtectedRoute><RewardDetail /></ProtectedRoute>
          } />
          <Route path="/profilo" element={
            <ProtectedRoute><AppLayout><Profilo /></AppLayout></ProtectedRoute>
          } />
          <Route path="/miei-riscatti" element={
            <ProtectedRoute><AppLayout><MieiRiscatti /></AppLayout></ProtectedRoute>
          } />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
const App = () => {
  const [showSplash, setShowSplash] = useState(true);

  return (
    <DesktopGate>
      {showSplash && (
        <SplashScreen
          onComplete={() => setShowSplash(false)}
          minDuration={1000}
        />
      )}
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <BrowserRouter>
            <AuthProvider>
              <Sonner />
              <ShadcnToaster />
              <ScrollToTop />
              <AnimatedRoutes />
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </DesktopGate>
  );
};

export default App;
